import { Component, OnInit } from '@angular/core';

import { Store, select } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';

import { ModalButtons, NotificationLayout } from '@fcom/ui-components';

import { getNumberOfFlightsInBound } from '../../utils';
import { Cabin, FlightsV2, FlightV2, Upgrade, UpgradeProduct, UpgradeStatus } from '../../api/models';
import { UpgradePaymentService } from '../../services/upgrade-payment.service';
import { State } from '../../interfaces/store.interface';
import { Stage } from '../../interfaces/stage.interface';
import { ModalActions, StatusActions } from '../../store/actions';
import { getBasketPassengerIds, getJourneys, getBasketFlights } from '../../store/selectors';
import { getUpgradeFromFlight } from '../../utils/data.utils';

export interface ExtendedFlightV2 extends FlightV2 {
  isBeingUpgraded: boolean;
  isWaitlisted: boolean;
  noUpgradesMessage: string;
  cabin: Cabin;
}

@Component({
  selector: 'fin-upgrade-review-bounds',
  templateUrl: 'review-bounds.component.html',
  styleUrls: ['review-bounds.component.scss'],
})
export class ReviewBoundsComponent implements OnInit {
  readonly NotificationLayout = NotificationLayout;
  readonly Cabin = Cabin;

  journeysWithUpgrades$: Observable<ExtendedFlightV2[][]>;
  selectedPassengers$: Observable<string[]>;

  constructor(
    private store$: Store<State>,
    private upgradePaymentService: UpgradePaymentService
  ) {}

  ngOnInit(): void {
    this.selectedPassengers$ = this.store$.pipe(select(getBasketPassengerIds));
    this.journeysWithUpgrades$ = combineLatest([
      this.store$.pipe(select(getJourneys)),
      this.store$.pipe(select(getBasketFlights)),
    ]).pipe(
      map(([journeys, basketFlights]) => {
        return journeys.map((journey) => {
          return journey.map((flight): ExtendedFlightV2 => {
            const matchingFlight = basketFlights.find(
              (selectedFlight) => flight.segmentInfo.flightId === selectedFlight.flightId
            );

            return {
              ...flight,
              isBeingUpgraded: Boolean(matchingFlight),
              noUpgradesMessage: !matchingFlight ? this.getNoUpgradesMessage(flight) : null,
              isWaitlisted: matchingFlight && getUpgradeFromFlight(flight, matchingFlight.productName)?.isWaitlist,
              cabin: matchingFlight?.cabin,
            };
          });
        });
      })
    );
  }

  printPrice(flight: FlightV2): Observable<string> {
    return this.upgradePaymentService.getPriceForSelectedFlight(flight);
  }

  getAllBoundsForJourney(bounds: FlightsV2): Observable<FlightsV2> {
    const idsInJourney = bounds.map((bound) => bound.segmentInfo.flightId);

    return this.journeysWithUpgrades$.pipe(
      map((journeys: FlightsV2[]) => {
        return journeys.find((flights: FlightsV2) => {
          return flights.some((bound) => idsInJourney.includes(bound.segmentInfo.flightId));
        });
      })
    );
  }

  getNumberOfFlights(flight: FlightV2, bounds: FlightsV2): number {
    return getNumberOfFlightsInBound(flight, bounds);
  }

  onFlightsChange(): void {
    this.store$.dispatch(StatusActions.setStage({ stage: Stage.FLIGHTS }));
  }

  openModal(event: Event, flightId: string): void {
    event.stopPropagation();

    this.store$.dispatch(
      ModalActions.openModal({
        modal: {
          title: 'oneUpgrade.upgradeSelection.waitlistInfoModal.title',
          fragment: 'fragments.oneUpgrade.waitlistHelp.url',
          buttons: ModalButtons.DONE,
        },
        returnTarget: `[data-flight="${flightId}"] fcom-notification button`,
      })
    );
  }

  getNoUpgradesMessage(flight: FlightV2): string {
    const upgradeNotAvailable = (upgrade?: Upgrade) => {
      return !upgrade || upgrade.upgradeStatus === UpgradeStatus.NotAvailable;
    };

    const noneAvailable = flight.upgrades.every((upgrade) => {
      return [UpgradeStatus.NotAvailable, UpgradeStatus.Failed].includes(upgrade.upgradeStatus);
    });

    if (noneAvailable) {
      return 'oneUpgrade.review.noneAvailableNotification';
    }

    const ecoToPE = getUpgradeFromFlight(flight, UpgradeProduct.EconomyToPremiumeconomy);
    const peToBiz = getUpgradeFromFlight(flight, UpgradeProduct.PremiumeconomyToBusiness);
    const ecoToBiz = getUpgradeFromFlight(flight, UpgradeProduct.EconomyToBusiness);

    if (ecoToPE?.upgradeStatus === UpgradeStatus.Waitlisted && upgradeNotAvailable(peToBiz)) {
      return 'oneUpgrade.review.waitlistedNotificationPremiumEconomy';
    }

    if (ecoToBiz?.upgradeStatus === UpgradeStatus.Waitlisted || peToBiz?.upgradeStatus === UpgradeStatus.Waitlisted) {
      return 'oneUpgrade.review.waitlistedNotificationBusiness';
    }

    if (ecoToPE?.upgradeStatus === UpgradeStatus.Success && upgradeNotAvailable(peToBiz)) {
      return 'oneUpgrade.review.confirmedNotificationPremiumEconomy';
    }

    if (ecoToBiz?.upgradeStatus === UpgradeStatus.Success || peToBiz?.upgradeStatus === UpgradeStatus.Success) {
      return 'oneUpgrade.review.confirmedNotificationBusiness';
    }

    return 'oneUpgrade.review.noUpgrades';
  }

  boundHasAvailableUpgrades(bound: ExtendedFlightV2[]): boolean {
    let hasUpgrades = false;

    bound.forEach((flight) => {
      flight.upgrades.forEach((upgrade) => {
        if (upgrade.upgradeStatus === UpgradeStatus.Available) {
          hasUpgrades = true;
        }
      });
    });

    return hasUpgrades;
  }
}
